import { useState, useEffect } from "react";
import { Paper, Typography, Button, Box, Snackbar, Alert, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PayMethodModal from "../PayMethodModal/PayMethodModal";
import PayMethodCard from "../PayMethodCard/PayMethodCard";
import "../../assets/style/PayMethod.css";
import api from "../../api/api";
import { useLanguage } from "../../context/LanguageContext";

import nothingImg from "../../assets/img/nothing.svg"

export default function PayMethod() {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    loadPayCards();
  }, []);

  const loadPayCards = async () => {
    setLoading(true);
    const userToken = sessionStorage.getItem("accessToken");
    
    // Проверяем, есть ли токен
    if (!userToken) {
      console.error("Токен не найден в sessionStorage");
      setNotification({
        show: true,
        message: 'Authentication required. Please log in again.',
        type: 'error'
      });
      setLoading(false);
      return;
    }
    
    try {
      const response = await api.get("/payment-methods", {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });
      let payMethodsObj = response.data;

      const formattedCards = payMethodsObj.cards.map((card) => ({
        ...card, // Копируем все свойства, которые пришли с сервера (id, brand, last4...)
        type: "card", // Добавляем наше свойство type для удобства рендеринга
      }));

      const formattedAccounts = payMethodsObj.accounts.map((account) => ({
        ...account, // Копируем все свойства счета (id, name...)
        type: "bank",
      }));

      const allMethods = [...formattedCards, ...formattedAccounts];
      setPaymentMethods(allMethods);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        console.log("Способы оплаты еще не добавлены.");
        setPaymentMethods([]); // Устанавливаем пустой массив, чтобы показать заглушку
      } else if (err.response && err.response.status === 401) {
        console.error("Unauthorized: Токен недействителен");
        setNotification({
          show: true,
          message: 'Session expired. Please log in again.',
          type: 'error'
        });
      } else {
        console.error("Произошла неожиданная ошибка при загрузке:", err);
        setNotification({
          show: true,
          message: 'Failed to load payment methods. Please try again.',
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMethod = async (methodId, methodType) => {
    const userToken = sessionStorage.getItem("accessToken");
    
    // Проверяем, есть ли токен
    if (!userToken) {
      console.error("Токен не найден в sessionStorage");
      setNotification({
        show: true,
        message: 'Authentication required. Please log in again.',
        type: 'error'
      });
      return;
    }
    
    console.log(methodType);
    
    try {
      await api.delete(`/payment-methods/${methodId}`, {
        headers: { Authorization: `Bearer ${userToken}` },
        data: { type: methodType },
      });
      
      // Удаляем из локального состояния
      setPaymentMethods((prevMethods) => prevMethods.filter((m) => m.id !== methodId));
      
      // Показываем уведомление об успехе
      setNotification({
        show: true,
        message: 'Payment method deleted successfully!',
        type: 'success'
      });
      
    } catch (err) {
      console.log(err);
      if (err.response && err.response.status === 401) {
        setNotification({
          show: true,
          message: 'Session expired. Please log in again.',
          type: 'error'
        });
      } else {
        setNotification({
          show: true,
          message: 'Failed to delete payment method. Please try again.',
          type: 'error'
        });
      }
    }
    
    // Скрываем уведомление через 5 секунд
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  // Функция для обработки успешного создания платежного метода
  const handlePaymentMethodCreated = () => {
    // Перезагружаем список платежных методов
    loadPayCards();
    
    // Показываем уведомление об успехе
    setNotification({
      show: true,
      message: 'Payment method added successfully!',
      type: 'success'
    });
    
    // Скрываем уведомление через 5 секунд
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  // Функция для закрытия уведомления
  const handleCloseNotification = () => {
    setNotification({ show: false, message: '', type: '' });
  };

  return (
    <>
      {/* Уведомление */}
      <Snackbar
        open={notification.show}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          icon={notification.type === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
          sx={{
            backgroundColor: notification.type === 'success' ? '#22c55e' : '#ef4444',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Paper elevation={0} className="card payment-methods-card">
        <div className="page-header">
          <div>
            <Typography variant="h5" className="card-title">
              {t('paymentMethods')}
            </Typography>
            <Typography variant="body2" className="card-subtitle">
              {t('paymentMethodsDesc')}
            </Typography>
          </div>
          <Button
            variant="contained"
            className="add-new-button"
            startIcon={<AddIcon />}
            onClick={() => setIsModalOpen(true)}
          >
            {t('addNew')}
          </Button>
        </div>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : paymentMethods.length > 0 ? (
          <div className="payment-methods-grid">
            {paymentMethods.map((method) => (
              <PayMethodCard
                key={method.id}
                method={method}
                onDelete={handleDeleteMethod}
              />
            ))}
          </div>
        ) : (
          <div className="placeholder-content">
            {/* <InboxOutlinedIcon className="placeholder-icon" /> */}
            <div className="nothing-img">
              <img src={nothingImg} alt="" />
            </div>
            <Typography variant="h6" className="placeholder-title">
              {t('nothingHere')}
            </Typography>
            <Typography variant="body2" className="card-subtitle">
              {t('addPaymentMethodToStart')}
            </Typography>
          </div>
        )}
      </Paper>

      <PayMethodModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onPaymentMethodCreated={handlePaymentMethodCreated}
      />
    </>
  );
}
